import {
  Box,
  CircularProgress,
  createStyles,
  Grid,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { colors, moneyFormat } from 'design-system';
import { uniqueId } from 'lodash';
import React, { useCallback } from 'react';

import { connect, ConnectedProps } from 'react-redux';

// Analytics
import {
  ITEM_CLICKED,
  LIST_CLICK,
  THREE_DOTS_MENU,
  trackEvent,
} from 'src/analytics';

// Data Layer
import { EMITTED } from 'data-layer-cr/dist/constants/quotationStatus';
import { QuotationSummaryResponse } from 'data-layer-cr/dist/models';
import { useNetworkObserver } from 'data-layer-cr/dist/network';

import { format } from 'date-fns';

// Redux
import PricingProfileResponse from 'src/models/responseTypes/PricingProfileResponse';
import {
  cloneAndEditQuotation,
  startCopyQuotation,
} from 'src/store/quotation/actions';
import { REQUEST_QUOTATION_CLONE } from 'src/store/quotation/requestIdentifiers';
import { RootState } from 'src/store/rootReducer';

// Functions
import { getRegularTitle } from 'src/functions/getSummaryTitle';
import renderRoleIcon from 'src/functions/renderRoleIcon';
import validateRole from 'src/functions/validateRole';
import shouldShowEditButton from '../shouldShowEditButton';

// Components
import StatusDisplay from '../../../../../StatusDisplay';
import QuotationEditButton from '../QuotationEditButton';
import QuotationMenuButton from '../QuotationMenuButton';

const useStyles = makeStyles(() =>
  createStyles({
    columnHeader: {
      color: colors.madison,
      fontWeight: 'bold',
      fontSize: 15,
    },
    titleUsername: {
      marginLeft: 10,
    },
    tableContainer: {
      borderRadius: 10,
    },
    customerNameColumn: {
      color: colors.bright_turquoise,
    },
    tableRow: {
      cursor: 'pointer',
    },
  }),
);

const { formatMoney } = moneyFormat;

export const QuotationTable = ({
  quotationList,
  cloningQuotationId,
  cloneAndEditQuotationAction,
  cardPressed,
  pricingProfiles,
  onModificateVin,
  startCopyQuotationAction,
}: Props): JSX.Element => {
  const classes = useStyles();
  const profiles = pricingProfiles;
  const cloningStatus = useNetworkObserver(REQUEST_QUOTATION_CLONE);

  const CheckIfCloning = useCallback(
    (quotationSummary: QuotationSummaryResponse) =>
      cloningStatus === 'requesting' &&
      cloningQuotationId === quotationSummary.id,
    [cloningStatus, cloningQuotationId],
  );

  const getProfileTypeOfMoney = (quotation: QuotationSummaryResponse) => {
    const quotationProfileId = quotation.profile.id;
    const CurProfile = profiles.find(
      (profile) => profile.id.toString() === quotationProfileId.toString(),
    ) as PricingProfileResponse;
    return CurProfile ? CurProfile.typeOfMoney : '';
  };

  const onEditButtonClick = useCallback(
    (quotationSummary: QuotationSummaryResponse) => {
      cloneAndEditQuotationAction(quotationSummary.id);
    },
    [cloneAndEditQuotationAction],
  );

  return (
    <Grid item xs={12}>
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead>
            {quotationList.length > 0 && (
              <TableRow>
                <TableCell align="left" className={classes.columnHeader}>
                  Cotización
                </TableCell>
                <TableCell align="left" className={classes.columnHeader}>
                  Estado
                </TableCell>
                <TableCell align="left" className={classes.columnHeader}>
                  Cliente
                </TableCell>
                <TableCell align="left" className={classes.columnHeader}>
                  Auto
                </TableCell>
                <TableCell align="left" className={classes.columnHeader}>
                  Prima
                </TableCell>
                <TableCell align="left" className={classes.columnHeader}>
                  Cotizado
                </TableCell>
                <TableCell align="left" className={classes.columnHeader} />
              </TableRow>
            )}
          </TableHead>
          <TableBody>
            {quotationList.map((curQuotationSummary): React.ReactNode => {
              const handleOpenDrawer = () => {
                cardPressed(curQuotationSummary.urlSalt);
              };
              const handleClickRow = () => {
                trackEvent({
                  eventName: THREE_DOTS_MENU,
                  eventProperties: {
                    [ITEM_CLICKED]: LIST_CLICK,
                  },
                });
                handleOpenDrawer();
              };
              return (
                <TableRow
                  key={uniqueId()}
                  onClick={handleClickRow}
                  className={classes.tableRow}
                  data-testing="table-row"
                >
                  <TableCell component="th" scope="row">
                    {validateRole(curQuotationSummary) && (
                      <Box
                        display="flex"
                        alignItems="center"
                        data-testing="quotation-role-column"
                      >
                        {renderRoleIcon(
                          curQuotationSummary.userRole,
                          curQuotationSummary.origin,
                          curQuotationSummary.fromPersonalLink,
                        )}
                        <Typography
                          className={classes.titleUsername}
                          variant="h4"
                        >
                          {getRegularTitle(curQuotationSummary)}
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="left" data-testing="status-display">
                    <StatusDisplay quotationSummary={curQuotationSummary} />
                  </TableCell>
                  <TableCell
                    align="left"
                    className={classes.customerNameColumn}
                    data-testing="customer-name-column"
                  >
                    {curQuotationSummary.customerName}
                  </TableCell>
                  <TableCell align="left" data-testing="car-information-column">
                    {`${curQuotationSummary.carBrand} ${curQuotationSummary.carModel}`}{' '}
                    / {curQuotationSummary.carYear}
                  </TableCell>
                  <TableCell align="left" data-testing="anual-payment-column">
                    {formatMoney(
                      curQuotationSummary.anualPayment,
                      true,
                      getProfileTypeOfMoney(curQuotationSummary),
                    )}
                  </TableCell>
                  <TableCell align="left" data-testing="data-column">
                    {format(
                      new Date(curQuotationSummary.quotationDate),
                      'dd/MM/yyyy',
                    )}
                  </TableCell>
                  <TableCell data-testing="buton-edit-column" align="center">
                    {shouldShowEditButton(curQuotationSummary) && (
                      <>
                        {!CheckIfCloning(curQuotationSummary) && (
                          <QuotationEditButton
                            data-testing="edit-button"
                            onConfirm={() =>
                              onEditButtonClick(curQuotationSummary)
                            }
                            onReject={(): void => {
                              /** empty function */
                            }}
                          />
                        )}
                        {CheckIfCloning(curQuotationSummary) && (
                          <CircularProgress
                            data-testid="cloning-network-progress"
                            size={12}
                          />
                        )}
                      </>
                    )}
                    {/* {curQuotationSummary.status.id === EMITTED && ( */}
                    <QuotationMenuButton
                      data-testid="quotation-menu-button"
                      onModificateVin={() =>
                        onModificateVin(curQuotationSummary.urlSalt)
                      }
                      onShowDocuments={handleOpenDrawer}
                      quotationId={curQuotationSummary.id}
                      onCopyQuotation={() =>
                        startCopyQuotationAction(
                          curQuotationSummary.urlSalt,
                          curQuotationSummary.id,
                        )
                      }
                      showVinOption={
                        curQuotationSummary.profile.type === 'colectiva'
                      }
                      isEmitted={curQuotationSummary.status.id === EMITTED}
                    />
                    {/* )} */}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Grid>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  cloningQuotationId: number;
  pricingProfiles: PricingProfileResponse[];
} => ({
  cloningQuotationId: state.quotation.cloningQuotationId,
  pricingProfiles: state.pricingProfiles.profiles,
});

const connector = connect(mapStateToProps, {
  cloneAndEditQuotationAction: cloneAndEditQuotation,
  startCopyQuotationAction: startCopyQuotation,
});
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  quotationList: QuotationSummaryResponse[];
  cardPressed: (quotationHash: string) => void;
  onModificateVin: (quotationHash: string) => void;
}

export default connector(QuotationTable);
